import { useEffect, useState } from "react";
import { doc, updateDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import Cropper from "react-easy-crop";
import imageCompression from "browser-image-compression";
import { db, storage } from "../../utils/firebase";
import { toast } from "react-toastify";


const maskEmail = (email) => {
  const [name, domain] = email.split("@");
  return name.slice(0, 2) + "***@" + domain;
};

const maskNumber = (num) => "******" + num.slice(-4);

export default function ProfileDrawer({
  open,
  onClose,
  userProfile,
  userEmail,
  onProfileUpdated,
}) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  // Crop states
  const [cropImage, setCropImage] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  /* ------------------ Load profile ------------------ */
  useEffect(() => {
    if (userProfile) {
      setForm({
        gstNumber: userProfile.gstNumber || "",
        address: userProfile.address || "",
        logo: userProfile.logo || "",

        homeDeliveryAvailable: userProfile.homeDeliveryAvailable ?? false,
        takeAwayAvailable: userProfile.takeAwayAvailable ?? false,
      });
    }
  }, [userProfile]);

  if (!open || !form) return null;

  const getCroppedImage = async (imageSrc, cropPixels) => {
    const image = new Image();
    image.src = imageSrc;
    await new Promise((r) => (image.onload = r));

    const canvas = document.createElement("canvas");
    canvas.width = cropPixels.width;
    canvas.height = cropPixels.height;
    const ctx = canvas.getContext("2d");

    ctx.drawImage(
      image,
      cropPixels.x,
      cropPixels.y,
      cropPixels.width,
      cropPixels.height,
      0,
      0,
      cropPixels.width,
      cropPixels.height
    );

    return new Promise((resolve) =>
      canvas.toBlob(resolve, "image/webp", 0.9)
    );
  };

  const uploadCompressedLogo = async (blob) => {
    try {
      const compressed = await imageCompression(blob, {
        maxSizeMB: 0.03, // 30 KB
        maxWidthOrHeight: 300,
        useWebWorker: true,
      });

      const logoRef = ref(
        storage,
        `logos/${userEmail}-${Date.now()}.webp`
      );

      await uploadBytes(logoRef, compressed);
      const url = await getDownloadURL(logoRef);

      setForm((prev) => ({ ...prev, logo: url }));
      toast.success("Logo updated");
    } catch (err) {
      console.error(err);
      toast.error("Logo upload failed");
    }
  };

  const handleLogoSelect = (file) => {
    if (!file) return;

    if (!["image/png", "image/jpeg", "image/jpg"].includes(file.type)) {
      toast.error("Only PNG or JPG images allowed");
      return;
    }

    const reader = new FileReader();
    reader.onload = () => setCropImage(reader.result);
    reader.readAsDataURL(file);
  };


  const handleSave = async () => {
    try {
      setSaving(true);

      await updateDoc(doc(db, "userDetails", userEmail), {
        gstNumber: form.gstNumber,
        address: form.address,
        logo: form.logo,

        homeDeliveryAvailable: form.homeDeliveryAvailable,
        takeAwayAvailable: form.takeAwayAvailable,
      });

      onProfileUpdated((prev) => ({
        ...prev,
        gstNumber: form.gstNumber,
        address: form.address,
        logo: form.logo,

        homeDeliveryAvailable: form.homeDeliveryAvailable,
        takeAwayAvailable: form.takeAwayAvailable,
      }));

      toast.success("Profile updated");
      onClose();
    } catch (err) {
      console.error(err);
      toast.error("Failed to save profile");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40"
        onClick={onClose}
      />

      {/* Drawer */}
      <div className="fixed right-0 top-0 h-full w-full max-w-md bg-white z-50 shadow-2xl flex flex-col">

        <div className="flex-1 overflow-y-auto p-6">
          {/* Header */}
          <div className="mb-6">
            <h2 className="text-xl font-semibold text-[#6cbb23]">
              Business Profile
            </h2>
            <p className="text-xs text-gray-500 mt-1">
              Manage your business information & logo - Transparent logos look best.
            </p>
          </div>

          {/* Logo Section */}
          <div className="flex items-center gap-4 mb-6">
            <div className="w-24 h-24 rounded-lg border bg-gray-50 overflow-hidden flex items-center justify-center">
              {form.logo ? (
                <img
                  src={form.logo}
                  alt="Business Logo"
                  className="w-full h-full object-cover"
                />
              ) : (
                <span className="text-xs text-gray-400">No Logo</span>
              )}
            </div>

            <label className="cursor-pointer text-sm font-medium text-[#6cbb23] hover:underline">
              Change Logo
              <input
                type="file"
                accept="image/png,image/jpeg,image/jpg"
                className="hidden"
                onChange={(e) => handleLogoSelect(e.target.files[0])}
              />
            </label>
          </div>

          {/* Read-only fields */}
          <div className="space-y-3">
            <input disabled value={userProfile.businessName} className="input bg-gray-50" />
            <input disabled value={maskEmail(userProfile.email)} className="input bg-gray-50" />
            <input disabled value={maskNumber(userProfile.phoneNumber)} className="input bg-gray-50" />
            <input disabled value={maskNumber(userProfile.whatsappNumber)} className="input bg-gray-50" />
          </div>

          {/* Editable Section */}
          <div className="mt-6 space-y-5">

            {/* Address */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Business Address
              </label>
              <textarea
                rows={3}
                placeholder="Enter complete business address"
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
                className="w-full border border-gray-300 rounded-lg p-3 text-sm
                 focus:ring-2 focus:ring-[#6cbb23]/40 focus:border-[#6cbb23]
                 outline-none resize-none bg-white"
              />
            </div>

            {/* GST Number */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                GST Number
              </label>
              <input
                placeholder="Enter GST number (optional)"
                value={form.gstNumber}
                onChange={(e) => setForm({ ...form, gstNumber: e.target.value.toUpperCase() })}
                className="w-full border border-gray-300 rounded-lg p-3 text-sm
                 focus:ring-2 focus:ring-[#6cbb23]/40 focus:border-[#6cbb23]
                 outline-none bg-white"
              />
              <p className="text-xs text-gray-400 mt-1">
                GST will be used for billing & reports
              </p>
            </div>

            {/* Service Availability - Only for Restaurants */}
            {userProfile.businessType === "Restaurant" && (
              <div className="space-y-4">

                {/* Home Delivery */}
                <div
                  className={`border rounded-xl p-4 transition-all ${form.homeDeliveryAvailable
                      ? "border-[#6cbb23] bg-[#6cbb23]/5"
                      : "border-gray-200 bg-white"
                    }`}
                >
                  <label className="flex items-center justify-between cursor-pointer">
                    <div className="pr-4">
                      <p className="text-sm font-semibold text-gray-800">
                        Home Delivery
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        Allow customers to place delivery orders
                      </p>
                    </div>

                    <div className="relative">
                      <input
                        type="checkbox"
                        checked={form.homeDeliveryAvailable}
                        onChange={(e) =>
                          setForm({ ...form, homeDeliveryAvailable: e.target.checked })
                        }
                        className="sr-only"
                      />
                      <div
                        className={`w-11 h-6 rounded-full transition-colors ${form.homeDeliveryAvailable ? "bg-[#6cbb23]" : "bg-gray-300"
                          }`}
                      />
                      <div
                        className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${form.homeDeliveryAvailable ? "translate-x-5" : ""
                          }`}
                      />
                    </div>
                  </label>
                </div>

                {/* Take Away */}
                {userProfile.businessSubType !== "service" && (
                  <div
                    className={`border rounded-xl p-4 transition-all ${form.takeAwayAvailable
                        ? "border-[#6cbb23] bg-[#6cbb23]/5"
                        : "border-gray-200 bg-white"
                      }`}
                  >
                    <label className="flex items-center justify-between cursor-pointer">
                      <div className="pr-4">
                        <p className="text-sm font-semibold text-gray-800">
                          Take Away
                        </p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          Allow customers to order for pickup
                        </p>
                      </div>

                      <div className="relative">
                        <input
                          type="checkbox"
                          checked={form.takeAwayAvailable}
                          onChange={(e) =>
                            setForm({ ...form, takeAwayAvailable: e.target.checked })
                          }
                          className="sr-only"
                        />
                        <div
                          className={`w-11 h-6 rounded-full transition-colors ${form.takeAwayAvailable ? "bg-[#6cbb23]" : "bg-gray-300"
                            }`}
                        />
                        <div
                          className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${form.takeAwayAvailable ? "translate-x-5" : ""
                            }`}
                        />
                      </div>
                    </label>
                  </div>
                )}
              </div>
            )}




          </div>
        </div>

        {/* Actions */}
        <div className="p-4 border-t border-gray-200 bg-white flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 py-2.5 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-100 transition"
          >
            Cancel
          </button>

          <button
            onClick={handleSave}
            disabled={saving}
            className="flex-1 py-2.5 rounded-lg bg-[#6cbb23] hover:bg-[#5aa81e] text-white font-medium shadow transition disabled:opacity-60"
          >
            {saving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </div>

      {/* Crop Modal (unchanged logic) */}
      {cropImage && (
        <div
          className="fixed inset-0 bg-black/70 z-[60] flex items-center justify-center"
          onClick={(e) => e.stopPropagation()}
        >
          <div
            className="bg-white w-full max-w-md rounded-xl p-4 space-y-4 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative h-64 bg-black rounded-lg overflow-hidden">
              <Cropper
                image={cropImage}
                crop={crop}
                zoom={zoom}
                aspect={1}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={(_, area) => setCroppedAreaPixels(area)}
              />
            </div>

            <input
              type="range"
              min={1}
              max={3}
              step={0.1}
              value={zoom}
              onChange={(e) => setZoom(e.target.value)}
            />

            <div className="flex gap-3">
              <button
                className="flex-1 py-2 rounded-lg border text-gray-600 hover:bg-gray-100"
                onClick={() => setCropImage(null)}
              >
                Cancel
              </button>

              <button
                className="flex-1 py-2 rounded-lg bg-[#6cbb23] hover:bg-[#5aa81e] text-white font-medium"
                onClick={async () => {
                  const blob = await getCroppedImage(
                    cropImage,
                    croppedAreaPixels
                  );
                  await uploadCompressedLogo(blob);
                  setCropImage(null);
                }}
              >
                Save Logo
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );

}
